/**
 * Effective participation / language / age for an Activity row
 * (docs/homepage-redesign-tech-design.md §2.4).
 *
 * Between migration A and the backfill the new columns are null. Every read goes
 * through these functions, and packages/database/scripts/homepage-backfill.ts
 * writes exactly what they return, so a null row behaves like its backfilled value.
 *
 * Self-contained on purpose: the backfill image copies this one file.
 */
import type {
  ActivityLanguageValue,
  ParticipationValue,
} from '../constants/participation.constants.js';

/** The only in-person activities taught in English when the columns were added (PRD §4.2). */
export const LEGACY_EN_ACTIVITY_TITLES: readonly string[] = [
  'Space & Science Sunday (Drop-in)',
  'Mama Beast Boot Camp',
  'Young Birders Club: Bird Feeder Workshop',
];

/** All recorded courses at launch are parent courses (PO decision, PRD §4.2). */
export const LEGACY_COURSE_AGE = { ageMin: 18, ageMax: 99 } as const;

/** The implicit age recorded courses were created with before the editor asked for one. */
export const COURSE_PLACEHOLDER_AGE = { ageMin: 0, ageMax: 99 } as const;

/** Statuses a recorded course can only reach after it was live once. */
const COURSE_ONCE_LIVE_STATUSES = ['APPROVED', 'ACTIVE', 'INACTIVE'];

export interface ActivityDefaultsInput {
  activityType?: string | null;
  participation?: ParticipationValue | null;
  language?: ActivityLanguageValue | null;
  childRequired?: boolean | null;
  titleCN?: string | null;
  titleEN?: string | null;
  ageMin: number;
  ageMax: number;
  status?: string | null;
  firstPublishedAt?: Date | string | null;
  createdAt?: Date | string | null;
}

const isRecorded = (a: ActivityDefaultsInput) => a.activityType === 'RECORDED';

export function isLegacyEnglishTitle(
  a: Pick<ActivityDefaultsInput, 'titleCN' | 'titleEN'>
): boolean {
  return LEGACY_EN_ACTIVITY_TITLES.some((t) => t === a.titleEN || t === a.titleCN);
}

export function effectiveParticipation(a: ActivityDefaultsInput): ParticipationValue {
  if (a.participation) return a.participation;
  if (isRecorded(a)) return 'ADULT_ONLY';
  return a.childRequired === false ? 'ADULT_ONLY' : 'PARENT_CHILD';
}

export function effectiveLanguage(a: ActivityDefaultsInput): ActivityLanguageValue {
  if (a.language) return a.language;
  if (isRecorded(a)) return 'ZH';
  return isLegacyEnglishTitle(a) ? 'EN' : 'ZH';
}

export function hasCoursePlaceholderAge(
  a: Pick<ActivityDefaultsInput, 'ageMin' | 'ageMax'>
): boolean {
  return a.ageMin === COURSE_PLACEHOLDER_AGE.ageMin && a.ageMax === COURSE_PLACEHOLDER_AGE.ageMax;
}

/**
 * Recorded courses carried a 0-99 placeholder age until the backfill set 18-99.
 * An age someone already edited is kept, by the backfill and by this fallback alike.
 */
export function effectiveAgeRange(a: ActivityDefaultsInput): { ageMin: number; ageMax: number } {
  if (isRecorded(a) && !a.participation && hasCoursePlaceholderAge(a))
    return { ...LEGACY_COURSE_AGE };
  return { ageMin: a.ageMin, ageMax: a.ageMax };
}

/**
 * When a recorded course first went live. Courses that were live before the column
 * existed fall back to createdAt, the value the backfill writes. Null for a course
 * that has never been live.
 */
export function effectiveFirstPublishedAt(
  a: Pick<ActivityDefaultsInput, 'activityType' | 'status' | 'firstPublishedAt' | 'createdAt'>
): Date | null {
  if (a.firstPublishedAt) return new Date(a.firstPublishedAt);
  if (a.activityType !== 'RECORDED' || !a.createdAt) return null;
  return a.status && COURSE_ONCE_LIVE_STATUSES.includes(a.status) ? new Date(a.createdAt) : null;
}
